import { TimeSection, DeliveryEligibility, BannerLocation, BannerConfig } from '../types'
import { ELIGIBILITY_MAP } from '../config/eligibilityConfig'
import { BANNER_CONFIG } from '../config/bannerConfig'
import { getDeliveryDayName, calculateCountdown, isSameDay } from './dateUtils'
import { replaceObjectPlaceholders } from './templateUtils'

/**
 * Calculate banner configuration for a given location and eligibility
 * Uses lazy evaluation to only calculate replacement values that are actually needed
 *
 * @param location - Banner location
 * @param eligibility - Current delivery eligibility
 * @returns Banner configuration with placeholders replaced or null
 */
export function calculateBannerConfig(
  location: BannerLocation,
  eligibility: DeliveryEligibility | null,
): BannerConfig | null {
  if (!eligibility) {
    return null
  }

  const config = BANNER_CONFIG[location]?.[eligibility]
  if (!config) {
    return null
  }

  // Lazily calculate only the replacement values that are actually needed
  const replacements = getLazyReplacementValues(config, eligibility)
  return replaceObjectPlaceholders(config, replacements)
}

/**
 * Determine delivery eligibility based on time section
 *
 * @param timeSection - The current time section
 * @returns The delivery eligibility type
 */
export function getDeliveryEligibility(timeSection: TimeSection): DeliveryEligibility {
  // Validate that timeSection exists in ELIGIBILITY_MAP
  if (Object.prototype.hasOwnProperty.call(ELIGIBILITY_MAP, timeSection)) {
    return ELIGIBILITY_MAP[timeSection]
  }

  return '3DD+'
}

/**
 * Determine delivery eligibility based on delivery date
 *
 * @param deliveryDate - The target delivery date
 * @returns Delivery eligibility type based on days until delivery
 */
export function getDeliveryEligibilityByDate(deliveryDate: Date | string): DeliveryEligibility {
  const targetDate = new Date(deliveryDate)

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)

  const dayAfterTomorrow = new Date()
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2)

  if (isSameDay(targetDate, tomorrow)) {
    return 'NDD'
  } else if (isSameDay(targetDate, dayAfterTomorrow)) {
    return '2DD'
  } else {
    return '3DD+'
  }
}

/**
 * Detect which placeholders are used in a banner config template
 *
 * @param config - Banner configuration object
 * @returns Object indicating which placeholders are present
 */
export function detectPlaceholders(config: BannerConfig): {
  needsDeliveryDay: boolean
  needsCountdown: boolean
} {
  const configStr = JSON.stringify(config)
  return {
    needsDeliveryDay: configStr.includes('{deliveryDay}'),
    needsCountdown: configStr.includes('{countdown}'),
  }
}

/**
 * Lazily calculate replacement values for banner template placeholders
 * Only calculates values that are actually needed by the template
 *
 * @param config - Banner configuration to inspect for placeholders
 * @param eligibility - The delivery eligibility type
 * @returns Object containing only the replacement values that are needed
 */
export function getLazyReplacementValues(
  config: BannerConfig,
  eligibility: DeliveryEligibility,
): Record<string, string> {
  const { needsDeliveryDay, needsCountdown } = detectPlaceholders(config)
  const replacements: Record<string, string> = {}

  // Only calculate deliveryDay if template uses {deliveryDay}
  if (needsDeliveryDay) {
    replacements.deliveryDay = getDeliveryDayName(eligibility)
  }

  // Only calculate countdown if template uses {countdown}
  if (needsCountdown) {
    replacements.countdown = calculateCountdown(eligibility)?.formattedTime || ''
  }

  return replacements
}
